import { compareReportSchema, debtsReportSchema, incomeExpenseReportSchema, spendingReportSchema, budgetMonthSchema, goalListSchema, goalSchema, limitListSchema, memberLimitSchema, recurringListResponseSchema, recurringTemplateSchema, currentRatesResponseSchema, exchangeRateEntrySchema, rateListResponseSchema, accountListResponseSchema, accountSchema, balanceSummarySchema, categoryListResponseSchema, categorySchema, authResponseSchema, CLIENT_TYPE_HEADER, createInvitationResponseSchema, familySchema, healthResponseSchema, invitationListResponseSchema, invitationPreviewSchema, memberDetailSchema, memberListResponseSchema, memberSchema, meResponseSchema, MOBILE_CLIENT_TYPE, problemDetailsSchema, payeeListResponseSchema, payeeSchema, transactionDefaultsSchema, transactionListResponseSchema, transactionSchema, debtDetailSchema, debtListResponseSchema, debtPaymentResponseSchema, personDetailSchema, personListResponseSchema, personSchema, } from '@familywallet/shared';
import { ApiError } from './errors.js';
/** `?a=1&b=2` from the defined values, or '' when there are none. */
function queryString(params) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params))
        if (value)
            query.set(key, value);
    return query.size > 0 ? `?${query.toString()}` : '';
}
const API_PREFIX = '/api/v1';
const NO_CONTENT = Symbol('no content');
/** Typed client for /api/v1. Holds the access token in memory only. */
export function createApiClient(options) {
    const clientType = options.clientType ?? 'web';
    let accessToken = null;
    let refreshing = null;
    const newKey = options.newIdempotencyKey ?? (() => globalThis.crypto.randomUUID());
    function setSignedIn(token) {
        const changed = (token !== null) !== (accessToken !== null);
        accessToken = token;
        if (changed)
            options.onAuthChange?.(token !== null);
    }
    async function send(path, init) {
        // Resolved per call (not captured once) so polyfills and test stubs installed later apply.
        const fetchImpl = options.fetch ?? globalThis.fetch;
        const headers = { Accept: 'application/json' };
        if (init.body !== undefined)
            headers['Content-Type'] = 'application/json';
        if (clientType === 'mobile')
            headers[CLIENT_TYPE_HEADER] = MOBILE_CLIENT_TYPE;
        if (init.auth !== false && accessToken)
            headers.Authorization = `Bearer ${accessToken}`;
        if (init.idempotencyKey)
            headers['Idempotency-Key'] = init.idempotencyKey;
        return fetchImpl(`${options.baseUrl}${API_PREFIX}${path}`, {
            method: init.method ?? 'GET',
            headers,
            body: init.body === undefined ? undefined : JSON.stringify(init.body),
            // The web refresh cookie is same-origin and scoped to /api/v1/auth.
            credentials: 'same-origin',
        });
    }
    async function parse(response, schema) {
        const body = response.status === 204 ? undefined : await response.json().catch(() => undefined);
        if (!response.ok) {
            const problem = problemDetailsSchema.safeParse(body);
            throw new ApiError(response.status, problem.success ? problem.data : undefined);
        }
        return schema === NO_CONTENT ? undefined : schema.parse(body);
    }
    async function request(path, schema, init = {}) {
        let response = await send(path, init);
        if (response.status === 401 && init.auth !== false) {
            // The access token expired (or was never loaded): refresh once, then retry once.
            if (await refreshSession())
                response = await send(path, init);
        }
        return parse(response, schema);
    }
    /** A file download (the reports PDF): the same auth and errors as `request`. */
    async function download(path) {
        let response = await send(path, {});
        if (response.status === 401) {
            if (await refreshSession())
                response = await send(path, {});
        }
        // An error response throws the API's problem details, as `request` does.
        if (!response.ok)
            await parse(response, NO_CONTENT);
        return response.blob();
    }
    async function storeTokens(tokens) {
        if (clientType === 'mobile')
            await options.refreshTokenStore?.set(tokens.refreshToken ?? null);
        setSignedIn(tokens.accessToken);
    }
    /** Signs in with an auth endpoint's response. */
    async function signIn(path, body) {
        const tokens = await request(path, authResponseSchema, { method: 'POST', body, auth: false });
        await storeTokens(tokens);
    }
    /**
     * Gets a new access token with the refresh token. Concurrent callers share one request, so a
     * burst of 401s never rotates the token twice (which would look like token theft).
     */
    function refreshSession() {
        refreshing ??= (async () => {
            try {
                let body = {};
                if (clientType === 'mobile') {
                    const stored = await options.refreshTokenStore?.get();
                    if (!stored) {
                        setSignedIn(null);
                        return false;
                    }
                    body = { refreshToken: stored };
                }
                const response = await send('/auth/refresh', { method: 'POST', body, auth: false });
                if (!response.ok) {
                    if (clientType === 'mobile' && response.status === 401)
                        await options.refreshTokenStore?.set(null);
                    setSignedIn(null);
                    return false;
                }
                await storeTokens(authResponseSchema.parse(await response.json()));
                return true;
            }
            catch {
                // Network failure: keep the stored refresh token, report signed out for now.
                setSignedIn(null);
                return false;
            }
            finally {
                refreshing = null;
            }
        })();
        return refreshing;
    }
    async function endSession() {
        if (clientType === 'mobile')
            await options.refreshTokenStore?.set(null);
        setSignedIn(null);
    }
    const create = (opts) => opts?.idempotencyKey ?? newKey();
    return {
        health: () => request('/health', healthResponseSchema, { auth: false }),
        /** True while an access token is held in memory. */
        isSignedIn: () => accessToken !== null,
        /** On app start: tries the saved refresh token (cookie or store). Resolves true if signed in. */
        restoreSession: () => refreshSession(),
        auth: {
            register: (body) => signIn('/auth/register', body),
            login: (body) => signIn('/auth/login', body),
            async logout() {
                const body = clientType === 'mobile'
                    ? { refreshToken: (await options.refreshTokenStore?.get()) ?? undefined }
                    : {};
                try {
                    await request('/auth/logout', NO_CONTENT, { method: 'POST', body, auth: false });
                }
                finally {
                    await endSession();
                }
            },
            /** Signs out every other device; this one keeps going with the fresh session returned. */
            async changePassword(body) {
                const tokens = await request('/auth/change-password', authResponseSchema, {
                    method: 'POST',
                    body,
                });
                await storeTokens(tokens);
            },
            async logoutAll() {
                await request('/auth/logout-all', NO_CONTENT, { method: 'POST' });
                await endSession();
            },
        },
        me: {
            get: () => request('/me', meResponseSchema),
            /** The owner also types the family name: deleting the owner deletes the whole family. */
            async deleteAccount(password, familyName) {
                await request('/me', NO_CONTENT, { method: 'DELETE', body: { password, familyName } });
                await endSession();
            },
        },
        family: {
            get: () => request('/family', familySchema),
            update: (body) => request('/family', familySchema, { method: 'PATCH', body }),
        },
        members: {
            list: (params = {}) => {
                const query = new URLSearchParams();
                if (params.cursor)
                    query.set('cursor', params.cursor);
                if (params.limit)
                    query.set('limit', String(params.limit));
                const suffix = query.size > 0 ? `?${query.toString()}` : '';
                return request(`/members${suffix}`, memberListResponseSchema);
            },
            get: (id) => request(`/members/${encodeURIComponent(id)}`, memberDetailSchema),
            create: (body, opts) => request('/members', memberSchema, { method: 'POST', body, idempotencyKey: create(opts) }),
            update: (id, body) => request(`/members/${encodeURIComponent(id)}`, memberDetailSchema, {
                method: 'PATCH',
                body,
            }),
            deactivate: (id) => request(`/members/${encodeURIComponent(id)}/deactivate`, memberDetailSchema, {
                method: 'POST',
            }),
            reactivate: (id) => request(`/members/${encodeURIComponent(id)}/reactivate`, memberDetailSchema, {
                method: 'POST',
            }),
            setPermissions: (id, body) => request(`/members/${encodeURIComponent(id)}/permissions`, memberDetailSchema, {
                method: 'PUT',
                body,
            }),
        },
        accounts: {
            list: (params = {}) => request(`/accounts${params.includeArchived ? '?includeArchived=true' : ''}`, accountListResponseSchema),
            get: (id) => request(`/accounts/${encodeURIComponent(id)}`, accountSchema),
            /** May record an opening balance, so it always carries an Idempotency-Key. */
            create: (body, opts) => request('/accounts', accountSchema, { method: 'POST', body, idempotencyKey: create(opts) }),
            update: (id, body) => request(`/accounts/${encodeURIComponent(id)}`, accountSchema, { method: 'PATCH', body }),
            archive: (id, body = {}) => request(`/accounts/${encodeURIComponent(id)}/archive`, accountSchema, {
                method: 'POST',
                body,
            }),
            unarchive: (id) => request(`/accounts/${encodeURIComponent(id)}/unarchive`, accountSchema, {
                method: 'POST',
            }),
            /** Adds a currency (e.g. LBP), maybe with an opening balance: always idempotent. */
            addCurrency: (id, body, opts) => request(`/accounts/${encodeURIComponent(id)}/currencies`, accountSchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
            /** Removes a currency whose balance is zero. */
            removeCurrency: (id, currency) => request(`/accounts/${encodeURIComponent(id)}/currencies/${encodeURIComponent(currency)}/remove`, accountSchema, { method: 'POST' }),
        },
        rates: {
            /** Today's rate per currency, whether it is old, and whether the viewer may add one. */
            current: () => request('/rates/current', currentRatesResponseSchema),
            list: (params = {}) => {
                const query = new URLSearchParams();
                if (params.quoteCurrency)
                    query.set('quoteCurrency', params.quoteCurrency);
                if (params.cursor)
                    query.set('cursor', params.cursor);
                if (params.limit)
                    query.set('limit', params.limit);
                const suffix = query.size > 0 ? `?${query.toString()}` : '';
                return request(`/rates${suffix}`, rateListResponseSchema);
            },
            create: (body, opts) => request('/rates', exchangeRateEntrySchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
        },
        categories: {
            list: (params = {}) => {
                const query = new URLSearchParams();
                if (params.kind)
                    query.set('kind', params.kind);
                if (params.q)
                    query.set('q', params.q);
                if (params.includeArchived)
                    query.set('includeArchived', params.includeArchived);
                const suffix = query.size > 0 ? `?${query.toString()}` : '';
                return request(`/categories${suffix}`, categoryListResponseSchema);
            },
            create: (body, opts) => request('/categories', categorySchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
            rename: (id, body) => request(`/categories/${encodeURIComponent(id)}`, categorySchema, { method: 'PATCH', body }),
            archive: (id) => request(`/categories/${encodeURIComponent(id)}/archive`, categorySchema, {
                method: 'POST',
            }),
            unarchive: (id) => request(`/categories/${encodeURIComponent(id)}/unarchive`, categorySchema, {
                method: 'POST',
            }),
        },
        transactions: {
            /** Newest first, only what the viewer may see; hidden accounts come back masked. */
            list: (params = {}) => {
                const query = new URLSearchParams();
                for (const [key, value] of Object.entries(params)) {
                    if (typeof value === 'string' && value !== '')
                        query.set(key, value);
                }
                const suffix = query.size > 0 ? `?${query.toString()}` : '';
                return request(`/transactions${suffix}`, transactionListResponseSchema);
            },
            /** One currency for another (always idempotent). */
            exchange: (body, opts) => request('/transactions/exchange', transactionSchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
            /** An expense paid in another currency: bill, paid, returned (always idempotent). */
            foreignExpense: (body, opts) => request('/transactions/foreign-expense', transactionSchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
            /** Correct one of those in one step (keeps its credit debt). */
            correctForeignExpense: (id, body, opts) => request(`/transactions/${id}/correct-foreign-expense`, transactionSchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
            /** The Add screen's last-used account per kind and currency per account. */
            defaults: () => request('/transactions/defaults', transactionDefaultsSchema),
            /** Records an expense, income or transfer: always idempotent. */
            create: (body, opts) => request('/transactions', transactionSchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
            get: (id) => request(`/transactions/${encodeURIComponent(id)}`, transactionSchema),
            /** Correct, first half: reverses the transaction (always idempotent). */
            reverse: (id, body = {}, opts) => request(`/transactions/${encodeURIComponent(id)}/reverse`, transactionSchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
            /** Correct an expense or income in one step (reversal + the right one; keeps its credit). */
            correct: (id, body, opts) => request(`/transactions/${encodeURIComponent(id)}/correct`, transactionSchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
        },
        debts: {
            /** Visible debts, open ones first, with totals for outside people per currency. */
            list: () => request('/debts', debtListResponseSchema),
            get: (id) => request(`/debts/${encodeURIComponent(id)}`, debtDetailSchema),
            /** Borrowed, lent or an existing debt: always idempotent. */
            create: (body, opts) => request('/debts', debtDetailSchema, { method: 'POST', body, idempotencyKey: create(opts) }),
            /** Repay or collect: always idempotent. */
            pay: (id, body, opts) => request(`/debts/${encodeURIComponent(id)}/payments`, debtPaymentResponseSchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
            /** Creates the installment plan, or changes it from the next open installment on. */
            setPlan: (id, body) => request(`/debts/${encodeURIComponent(id)}/plan`, debtDetailSchema, {
                method: 'PUT',
                body,
            }),
            /** Skips an open installment (moves no money). */
            skipInstallment: (id, number) => request(`/debts/${encodeURIComponent(id)}/installments/${number}/skip`, debtDetailSchema, {
                method: 'POST',
            }),
        },
        recurring: {
            /** Templates the viewer may record, with their due occurrences and the late notice. */
            list: () => request('/recurring', recurringListResponseSchema),
            create: (body) => request('/recurring', recurringTemplateSchema, { method: 'POST', body }),
            /** Changes the template from today (or after the last recorded occurrence) on. */
            update: (id, body) => request(`/recurring/${encodeURIComponent(id)}`, recurringTemplateSchema, {
                method: 'PUT',
                body,
            }),
            setPaused: (id, paused) => request(`/recurring/${encodeURIComponent(id)}/${paused ? 'pause' : 'resume'}`, recurringTemplateSchema, { method: 'POST' }),
            /** Deletes the template; transactions recorded from it stay. */
            remove: (id) => request(`/recurring/${encodeURIComponent(id)}`, NO_CONTENT, { method: 'DELETE' }),
            skip: (id, dueOn) => request(`/recurring/${encodeURIComponent(id)}/occurrences/${encodeURIComponent(dueOn)}/skip`, recurringTemplateSchema, { method: 'POST' }),
            /** "Skip all older": every open occurrence due before `dueOn`. */
            skipOlder: (id, dueOn) => request(`/recurring/${encodeURIComponent(id)}/skip-older`, recurringTemplateSchema, {
                method: 'POST',
                body: { dueOn },
            }),
        },
        budgets: {
            /** One month (default: this month) in the base currency; spent counts what you can see. */
            month: (month) => request(`/budgets${month ? `?month=${encodeURIComponent(month)}` : ''}`, budgetMonthSchema),
            /** Sets one budget (categoryId null: overall) from this month on; "0" removes it. */
            set: (body) => request('/budgets', budgetMonthSchema, { method: 'PUT', body }),
        },
        /**
         * Reports (Phase 6), in the base currency. With reports.view: the family, or one member
         * (`memberId`); without it, the viewer's own spending and debts.
         */
        reports: {
            spending: (params = {}) => request(`/reports/spending${queryString(params)}`, spendingReportSchema),
            compare: (params = {}) => request(`/reports/compare${queryString(params)}`, compareReportSchema),
            incomeExpense: (params = {}) => request(`/reports/income-expense${queryString(params)}`, incomeExpenseReportSchema),
            debts: (params = {}) => request(`/reports/debts${queryString(params)}`, debtsReportSchema),
            /** The month's four reports as a PDF (only what the viewer can see); 5 a minute. */
            pdf: (params = {}) => download(`/reports/pdf${queryString(params)}`),
        },
        limits: {
            /** Parents: every child's monthly limit; a child: their own. */
            list: () => request('/limits', limitListSchema),
            /** Sets a child's limit from this month on; "0" removes it. */
            set: (memberId, amount) => request(`/limits/${encodeURIComponent(memberId)}`, memberLimitSchema, {
                method: 'PUT',
                body: { amount },
            }),
        },
        goals: {
            list: (includeArchived = false) => request(`/goals${includeArchived ? '?includeArchived=true' : ''}`, goalListSchema),
            get: (id) => request(`/goals/${encodeURIComponent(id)}`, goalSchema),
            create: (body) => request('/goals', goalSchema, { method: 'POST', body }),
            update: (id, body) => request(`/goals/${encodeURIComponent(id)}`, goalSchema, { method: 'PATCH', body }),
            setArchived: (id, archived) => request(`/goals/${encodeURIComponent(id)}/${archived ? 'archive' : 'unarchive'}`, goalSchema, { method: 'POST' }),
        },
        people: {
            /** Most used first; people of debts the viewer cannot see never appear. */
            list: (params = {}) => {
                const query = new URLSearchParams();
                if (params.q)
                    query.set('q', params.q);
                if (params.includeArchived)
                    query.set('includeArchived', params.includeArchived);
                if (params.limit)
                    query.set('limit', params.limit);
                const suffix = query.size > 0 ? `?${query.toString()}` : '';
                return request(`/people${suffix}`, personListResponseSchema);
            },
            get: (id) => request(`/people/${encodeURIComponent(id)}`, personDetailSchema),
            create: (body, opts) => request('/people', personSchema, { method: 'POST', body, idempotencyKey: create(opts) }),
            update: (id, body) => request(`/people/${encodeURIComponent(id)}`, personSchema, { method: 'PATCH', body }),
            archive: (id) => request(`/people/${encodeURIComponent(id)}/archive`, personSchema, { method: 'POST' }),
            unarchive: (id) => request(`/people/${encodeURIComponent(id)}/unarchive`, personSchema, { method: 'POST' }),
            merge: (id, body) => request(`/people/${encodeURIComponent(id)}/merge`, personSchema, { method: 'POST', body }),
        },
        payees: {
            /** Most used first; only payees used in transactions the viewer can see (or unused). */
            list: (params = {}) => {
                const query = new URLSearchParams();
                if (params.q)
                    query.set('q', params.q);
                if (params.limit)
                    query.set('limit', params.limit);
                if (params.includeArchived)
                    query.set('includeArchived', params.includeArchived);
                const suffix = query.size > 0 ? `?${query.toString()}` : '';
                return request(`/payees${suffix}`, payeeListResponseSchema);
            },
            create: (body, opts) => request('/payees', payeeSchema, { method: 'POST', body, idempotencyKey: create(opts) }),
            /** Rename or set the default category (payees.manage). */
            update: (id, body) => request(`/payees/${encodeURIComponent(id)}`, payeeSchema, { method: 'PATCH', body }),
            archive: (id) => request(`/payees/${encodeURIComponent(id)}/archive`, payeeSchema, { method: 'POST' }),
            unarchive: (id) => request(`/payees/${encodeURIComponent(id)}/unarchive`, payeeSchema, { method: 'POST' }),
            /** The duplicate's transactions read as the other payee; cannot be undone. */
            merge: (id, body) => request(`/payees/${encodeURIComponent(id)}/merge`, payeeSchema, { method: 'POST', body }),
        },
        balances: {
            summary: () => request('/balances/summary', balanceSummarySchema),
        },
        invitations: {
            create: (body, opts) => request('/invitations', createInvitationResponseSchema, {
                method: 'POST',
                body,
                idempotencyKey: create(opts),
            }),
            listPending: () => request('/invitations', invitationListResponseSchema),
            revoke: (id) => request(`/invitations/${encodeURIComponent(id)}`, NO_CONTENT, { method: 'DELETE' }),
            preview: (token) => request(`/invitations/preview/${encodeURIComponent(token)}`, invitationPreviewSchema, {
                auth: false,
            }),
            accept: (body) => signIn('/invitations/accept', body),
        },
    };
}
//# sourceMappingURL=client.js.map